'use client'

import { useEffect, useRef, useMemo } from 'react'
import Link from 'next/link'
import { ArrowRight, Clock } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { getRelatedTools, type ToolManifestEntry } from '@/data/tools-manifest'
import { ToolIllustration } from '@/components/tools/tool-illustration'
import { toolNavigationAnalytics } from '@/lib/analytics'
import { useSavedInputs } from '@/hooks/use-saved-inputs'
import { STAGGER_CONTAINER, STAGGER_ITEM } from '@/lib/motion-presets'
import { buildRelatedToolUrl, type ToolUrlState } from '@/lib/result-tool-urls'

interface RelatedToolsProps {
  currentToolId: string
  prefillValues?: ToolUrlState
  placement?: string
  className?: string
}

const RECOMMENDATION_REASONS: Record<string, Record<string, string>> = {
  'lawn-size-calculator': {
    'fertilizer-calculator': 'Get the right amount of product for your lawn',
    'watering-schedule': 'Set watering zones based on your lawn dimensions',
    'lawn-cost-estimator': 'Estimate costs now that you know your square footage',
    'sod-calculator': 'Turn your square footage into sod pallets and cost',
  },
  'soil-temperature': {
    'herbicide-timing': 'Apply pre-emergent at the right soil temp',
    'seeding-calculator': 'Seed when soil temps hit the sweet spot',
    'fertilizer-calculator': 'Time your first app when the soil is ready',
  },
  'seeding-calculator': {
    'watering-schedule': 'Keep new seed moist with the right schedule',
    'soil-temperature': 'Verify soil temps before you seed',
    'fertilizer-calculator': 'Feed new grass at the right stage',
    'overseeding-calculator': 'Thickening an existing lawn? Get your overseed rate',
    'sod-calculator': 'Want instant results instead? Price out sod',
  },
  'fertilizer-calculator': {
    'watering-schedule': 'Water in your fertilizer at the right depth',
    'soil-test-analyzer': 'Test your soil to fine-tune NPK ratios',
    'lime-calculator': 'Get a precise lime rate from your soil pH and texture',
    'mowing-calculator': 'Mow at the right height after feeding',
  },
  'watering-schedule': {
    'fertilizer-calculator': 'Pair watering with a feeding schedule',
    'mowing-calculator': 'Adjust mowing height for your watering level',
    'soil-temperature': 'Check if soil temps affect water uptake',
  },
  'lawn-cost-estimator': {
    'lawn-size-calculator': 'Measure your lawn for more accurate cost estimates',
    'fertilizer-calculator': 'See exactly how much fertilizer you need',
    'watering-schedule': 'Factor in water usage for your budget',
    'artificial-turf-cost-calculator': 'Price a no-mow turf yard against annual lawn care',
  },
  'artificial-turf-cost-calculator': {
    'sod-calculator': 'Compare the cost of real sod against fake grass',
    'lawn-cost-estimator': 'Compare turf against the yearly cost of a real lawn',
    'lawn-size-calculator': 'Measure your area for a more accurate turf estimate',
  },
  'mowing-calculator': {
    'watering-schedule': 'Match watering to your mowing frequency',
    'fertilizer-calculator': 'Feed your lawn based on growth rate',
    'grass-comparison': 'Compare grass types and their mowing needs',
  },
  'aeration-timing-tool': {
    'seeding-calculator': 'Overseed right after aeration for best results',
    'fertilizer-calculator': 'Fertilize after aerating for deeper nutrient reach',
    'soil-temperature': 'Time aeration when soil temps are ideal',
    'overseeding-calculator': 'Overseed into fresh aeration holes for better contact',
    'topsoil-calculator': 'Topdress after aeration to level and enrich',
  },
  'sprinkler-cost-calculator': {
    'watering-schedule': 'Dial in inches per week once the system is in',
    'lawn-size-calculator': 'Measure your lawn for an accurate zone count',
    'lawn-cost-estimator': 'See the full annual lawn care budget picture',
  },
  'fertilizer-rate-calculator': {
    'fertilizer-calculator': 'See when to feed with the 12-month calendar',
    'lawn-size-calculator': 'Measure your lawn for an accurate total',
    'soil-test-analyzer': 'Find out which nutrients your soil actually needs',
  },
  'tank-mix-calculator': {
    'herbicide-timing': 'Check when to spray before you mix',
    'soil-temperature': 'Verify soil temps support your application',
    'lawn-size-calculator': 'Measure your lawn for blanket application coverage',
  },
  'herbicide-timing': {
    'tank-mix-calculator': 'Get exact per-gallon mix rates for your sprayer',
    'soil-temperature': 'Track soil temps for precise herbicide timing',
    'fertilizer-calculator': 'Plan fertilizer around your herbicide schedule',
    'mowing-calculator': 'Adjust mowing before and after herbicide apps',
  },
  'soil-test-analyzer': {
    'fertilizer-calculator': 'Apply the exact nutrients your soil needs',
    'lawn-size-calculator': 'Measure your lawn for accurate amendment rates',
    'watering-schedule': 'Adjust watering based on soil composition',
    'lime-calculator': 'Correct your soil pH with the right lime rate',
  },
  'grass-comparison': {
    'seeding-calculator': 'Check if it is the right time to seed your pick',
    'mowing-calculator': 'Get mowing specs for the grass you chose',
    'watering-schedule': 'Set up watering for your selected grass type',
  },
  'sod-calculator': {
    'lawn-size-calculator': 'Measure your yard for an exact sod count',
    'overseeding-calculator': 'Going the seed route? Get your overseeding rate',
    'fertilizer-calculator': 'Feed new sod once it roots in',
    'artificial-turf-cost-calculator': 'Weigh fake grass against real sod on cost and upkeep',
  },
  'topsoil-calculator': {
    'lawn-size-calculator': 'Measure your lawn for accurate soil volume',
    'aeration-timing-tool': 'Aerate before topdressing for better soil contact',
    'overseeding-calculator': 'Overseed right after leveling for a thicker lawn',
  },
  'grub-control-calculator': {
    'herbicide-timing': 'Time weed control around your grub treatment',
    'weed-and-feed-calculator': 'Handle weeds and feeding in one pass',
    'soil-temperature': 'Treat grubs when soil temps bring them near the surface',
  },
  'dethatching-calculator': {
    'aeration-timing-tool': 'Aerate after dethatching to open up the soil',
    'overseeding-calculator': 'Overseed into freshly dethatched turf',
    'soil-temperature': 'Dethatch when soil temps favor recovery',
  },
  'weed-and-feed-calculator': {
    'herbicide-timing': 'Nail the window for weed control',
    'overseeding-calculator': 'Wait out the herbicide before you overseed',
    'fertilizer-calculator': 'Dial in a full-season feeding plan',
  },
  'overseeding-calculator': {
    'seeding-calculator': 'Seeding a bare lawn instead? Get your seeding window',
    'aeration-timing-tool': 'Aerate first so overseed makes solid soil contact',
    'topsoil-calculator': 'Topdress low spots before you overseed',
  },
  'fertilizer-schedule-calculator': {
    'fertilizer-calculator': 'Get exact product amounts for each feeding',
    'fertilizer-rate-calculator': 'Convert your plan into precise per-app rates',
    'soil-temperature': 'Start feeding once the soil warms up',
  },
  'spreader-settings': {
    'fertilizer-rate-calculator': 'Turn the label rate into pounds for your lawn',
    'weed-and-feed-calculator': 'Check the timing and grass safety before you spread',
    'fertilizer-schedule-calculator': 'Plan the whole season around this product',
  },
  'lime-calculator': {
    'soil-test-analyzer': 'Read your full soil test for nutrient gaps',
    'fertilizer-schedule-calculator': 'Plan your feedings once pH is on track',
    'soil-temperature': 'Time lime around soil temperature swings',
  },
}

function getReasonText(sourceId: string, targetId: string, targetDescription: string): string {
  const reason = RECOMMENDATION_REASONS[sourceId]?.[targetId]
  if (reason) return reason
  return targetDescription.length > 60
    ? targetDescription.slice(0, 57) + '...'
    : targetDescription
}

const TARGET_CARRYOVER_KEYS: Record<string, Array<keyof ToolUrlState>> = {
  'lawn-size-calculator': ['zip', 'lawnSize'],
  'soil-temperature': ['zip', 'location'],
  'grass-comparison': ['grassType', 'grasses', 'zip'],
  'soil-test-analyzer': ['zip', 'grassType', 'lawnSize'],
  'mowing-calculator': ['grassType', 'sunlight', 'season'],
  'fertilizer-calculator': ['grassType'],
}

const DEFAULT_CARRYOVER_KEYS: Array<keyof ToolUrlState> = [
  'zip',
  'grassType',
  'lawnSize',
  'soilType',
  'sunlight',
  'zones',
]

const CARRYOVER_LABELS: Partial<Record<keyof ToolUrlState, string>> = {
  zip: 'ZIP',
  location: 'location',
  grassType: 'grass',
  grasses: 'grass picks',
  lawnSize: 'size',
  soilType: 'soil',
  sunlight: 'sunlight',
  zones: 'zones',
  season: 'season',
}

function getPrefillFields(prefill?: ToolUrlState, targetToolId?: string): string[] {
  if (!prefill) return []
  const keys = targetToolId ? TARGET_CARRYOVER_KEYS[targetToolId] ?? DEFAULT_CARRYOVER_KEYS : DEFAULT_CARRYOVER_KEYS
  return keys
    .filter((key) => {
      const value = prefill[key]
      if (Array.isArray(value)) return value.length > 0
      return value !== undefined && value !== null && value !== ''
    })
    .map((key) => CARRYOVER_LABELS[key] ?? String(key))
}

export function RelatedTools({
  currentToolId,
  prefillValues: explicitPrefill,
  placement = 'after_results',
  className,
}: RelatedToolsProps) {
  const viewedRef = useRef(false)
  const sectionRef = useRef<HTMLElement | null>(null)
  const tools = getRelatedTools(currentToolId)
  const { savedInputs } = useSavedInputs()
  const reduce = useReducedMotion()

  // Auto-read from localStorage when no explicit prefill is passed
  const prefillValues = useMemo(() => {
    if (explicitPrefill) return explicitPrefill
    const auto: RelatedToolsProps['prefillValues'] = {}
    if (savedInputs.zip) auto.zip = savedInputs.zip
    if (savedInputs.grassType) auto.grassType = savedInputs.grassType
    if (savedInputs.lawnSize) auto.lawnSize = savedInputs.lawnSize
    if (savedInputs.soilType) auto.soilType = savedInputs.soilType
    if (savedInputs.sunlight) auto.sunlight = savedInputs.sunlight
    return Object.keys(auto).length > 0 ? auto : undefined
  }, [explicitPrefill, savedInputs.zip, savedInputs.grassType, savedInputs.lawnSize, savedInputs.soilType, savedInputs.sunlight])

  useEffect(() => {
    if (viewedRef.current || tools.length === 0) return

    const trackViewed = () => {
      if (viewedRef.current) return
      viewedRef.current = true
      toolNavigationAnalytics.relatedToolsViewed({
        sourceTool: currentToolId,
        placement,
        toolCount: tools.length,
      })
    }

    const node = sectionRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      trackViewed()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        trackViewed()
        observer.disconnect()
      },
      { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [currentToolId, placement, tools.length])

  if (tools.length === 0) return null

  const handleClick = (target: ToolManifestEntry, rank: number) => {
    const reason = getReasonText(currentToolId, target.id, target.description)
    const prefillFields = getPrefillFields(prefillValues, target.id)

    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: currentToolId,
      targetTool: target.id,
      rank,
      placement,
      recommendationReason: reason,
      prefillFields: prefillFields.join(','),
    })
  }

  return (
    <section ref={sectionRef} className={cn('py-10', className)}>
      <div className="mb-8 text-center">
        <div className="text-xs font-bold uppercase tracking-wider text-green-600">
          What to do next
        </div>
        <h2 className="mt-1.5 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          These tools pick up where your results left off
        </h2>
      </div>

      <motion.div
        className="grid grid-cols-1 gap-5 md:grid-cols-3"
        variants={reduce ? undefined : STAGGER_CONTAINER}
        initial={reduce ? undefined : "hidden"}
        whileInView={reduce ? undefined : "show"}
        viewport={{ once: true, amount: 0.2 }}
      >
        {tools.map((tool, i) => {
          const reason = getReasonText(currentToolId, tool.id, tool.description)
          const href = buildRelatedToolUrl(tool.id, currentToolId, prefillValues ?? {})
          const hasPrefill = getPrefillFields(prefillValues, tool.id).length > 0

          return (
            <motion.div
              key={tool.id}
              variants={reduce ? undefined : STAGGER_ITEM}
              whileHover={reduce ? undefined : { y: -4 }}
              className="h-full"
            >
              <Link
                href={href}
                onClick={() => handleClick(tool, i + 1)}
                className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_12px_40px_rgba(0,0,0,0.04)] transition-[box-shadow,border-color] duration-200 hover:border-green-200 hover:shadow-[0_24px_50px_-16px_rgba(16,40,24,0.16)]"
              >
                <div className="flex h-16 w-16 items-center justify-center [&_svg]:h-16 [&_svg]:w-16">
                  <ToolIllustration href={tool.href} />
                </div>
                <p className="mt-4 text-lg font-bold leading-snug tracking-tight text-gray-900 transition-colors group-hover:text-green-700">
                  {tool.name}
                </p>
                <p className="mt-1.5 flex-1 text-sm leading-relaxed text-gray-600">
                  {reason}
                </p>
                <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-3.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                    <span>{hasPrefill ? `${tool.estimatedTime}, prefilled` : tool.estimatedTime}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                    Start
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            </motion.div>
          )
        })}
      </motion.div>
    </section>
  )
}
